// src/pages/ManagerReport.jsx
import { useState, useEffect } from 'react';
import { api } from '../api';
import { useApp } from '../AppContext';
import { Card, SectionTitle, PageHeader, Table, TR, TD, Badge, Btn, Spinner, Empty, Flash } from '../components/UI';

export default function ManagerReport() {
  const { site, user } = useApp();
  const perms = user?.permissions || {};
  const [tab, setTab]         = useState('signins');
  const [rows, setRows]       = useState([]);
  const [parties, setParties] = useState([]);
  const [guests, setGuests]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState('50');
  const [dateFilter, setDateFilter] = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [q, setQ]                   = useState('');
  const [loading, setLoading]       = useState(false);
  const [revenue, setRevenue]       = useState(null);
  const [flash, setFlash]           = useState(null);

  const features = site?.features || {};

  function fetchSignins(p = page) {
    setLoading(true);
    let url = `/api/signins/report?page=${p}&per_page=${perPage}`;
    if (dateFilter) url += `&date=${dateFilter}`;
    else { if (dateFrom) url += `&date_from=${dateFrom}`; if (dateTo) url += `&date_to=${dateTo}`; }
    if (q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
    api.get(url).then(r => {
      if (r?.ok) { setRows(r.data.rows || []); setTotal(r.data.total); setTotalPages(r.data.total_pages); setPage(p); }
      setLoading(false);
    });
  }

  useEffect(() => { fetchSignins(1); }, [perPage]);

  useEffect(() => {
    api.get('/api/parties?order=desc').then(r => r?.ok && setParties(r.data.parties || []));
    if (features.guest_id) {
      api.get('/api/guests').then(r => r?.ok && setGuests(r.data.guests || []));
    }
  }, []);

  async function fetchRevenue() {
    const r = await api.get('/api/signins/revenue');
    if (r?.ok) setRevenue(r.data);
  }

  async function deleteSigning(id) {
    if (!window.confirm('Delete this sign-in record?')) return;
    const r = await api.post(`/api/signins/${id}/delete`, {});
    if (r?.ok) { setFlash({ type: 'success', msg: 'Sign-in deleted.' }); fetchSignins(); }
    else setFlash({ type: 'error', msg: 'Failed to delete.' });
  }

  const tabStyle = (t) => ({
    padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
    border: 'none', fontFamily: 'Arial, sans-serif',
    background: tab === t ? 'var(--accent)' : 'var(--bg2)',
    color: tab === t ? '#fff' : 'var(--fg2)',
    borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid var(--border)',
  });

  return (
    <div style={{ padding: '20px 16px', maxWidth: 1200, margin: '0 auto' }}>
      <PageHeader
        title="Manager Report"
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a href={`${api.flaskUrl('/manager-report/export/csv')}?date=${dateFilter}&date_from=${dateFrom}&date_to=${dateTo}&q=${encodeURIComponent(q.trim())}`}
              style={{ padding: '7px 12px', fontSize: 12, fontWeight: 'bold', color: 'var(--accent)', border: '1px solid var(--accent)', textDecoration: 'none' }}>Export CSV</a>
            <a href={`${api.flaskUrl('/manager-report/export/pdf')}?date=${dateFilter}&date_from=${dateFrom}&date_to=${dateTo}&q=${encodeURIComponent(q.trim())}`}
              style={{ padding: '7px 12px', fontSize: 12, fontWeight: 'bold', color: 'var(--accent)', border: '1px solid var(--accent)', textDecoration: 'none' }}>Export PDF</a>
          </div>
        }
      />

      {flash && <Flash type={flash.type} onDismiss={() => setFlash(null)}>{flash.msg}</Flash>}

      {/* Tabs */}
      <div style={{ display: 'flex', marginBottom: 16, borderBottom: '2px solid var(--border)' }}>
        <button style={tabStyle('signins')} onClick={() => setTab('signins')}>Sign-Ins</button>
        <button style={tabStyle('parties')} onClick={() => setTab('parties')}>Parties</button>
        {features.guest_id && <button style={tabStyle('guests')} onClick={() => setTab('guests')}>Guest Log</button>}
        {features.revenue && <button style={tabStyle('revenue')} onClick={() => { setTab('revenue'); fetchRevenue(); }}>Revenue</button>}
      </div>

      {/* Sign-ins tab */}
      {tab === 'signins' && (
        <Card>
          {/* Filters */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--fg3)', marginBottom: 3 }}>Single date</div>
              <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--fg3)', marginBottom: 3 }}>From</div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} disabled={!!dateFilter}
                style={{ padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13, opacity: dateFilter ? 0.5 : 1 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--fg3)', marginBottom: 3 }}>To</div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} disabled={!!dateFilter}
                style={{ padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13, opacity: dateFilter ? 0.5 : 1 }} />
            </div>
            <select value={perPage} onChange={e => setPerPage(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13 }}>
              <option value="10">10 / page</option>
              <option value="50">50 / page</option>
              <option value="100">100 / page</option>
              <option value="all">All</option>
            </select>
            <div>
              <div style={{ fontSize: 11, color: 'var(--fg3)', marginBottom: 3 }}>Search (name or mbr #)</div>
              <input type="text" value={q} onChange={e => setQ(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchSignins(1); }}
                placeholder="Name or membership #"
                style={{ padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13, width: 170 }} />
            </div>
            <Btn onClick={() => fetchSignins(1)}>Search</Btn>
            <Btn variant="ghost" onClick={() => { setDateFilter(''); setDateFrom(''); setDateTo(''); setQ(''); setTimeout(() => fetchSignins(1), 0); }}>Clear</Btn>
            <span style={{ fontSize: 12, color: 'var(--fg3)', marginLeft: 'auto' }}>{total} records</span>
          </div>

          {loading ? <Spinner /> : rows.length === 0 ? <Empty message="No records found." /> : (
            <Table headers={['Date', 'MBR #', 'Last Name', 'Type', 'Members', 'Guests', 'Method', 'Total', 'Guard', '']}>
              {rows.map(r => (
                <TR key={r.id}>
                  <TD>{r.signin_date}</TD>
                  <TD>{r.membership_number}</TD>
                  <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{r.lastname}</TD>
                  <TD>{r.membership_type}</TD>
                  <TD>{r.member_people}</TD>
                  <TD>{r.guest_count || '—'}</TD>
                  <TD><Badge variant={r.payment_method === 'card' ? 'blue' : 'green'}>{r.payment_method}</Badge></TD>
                  <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>${(r.total_charge || 0).toFixed(2)}</TD>
                  <TD>{r.guard_name}</TD>
                  {perms.can_delete_signins && <TD><button onClick={() => deleteSigning(r.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: 11 }}>Delete</button></TD>}
                </TR>
              ))}
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 4, padding: '12px 16px', justifyContent: 'center', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
              {page > 1 && <Btn variant="outline" style={{ fontSize: 11 }} onClick={() => fetchSignins(page - 1)}>Prev</Btn>}
              {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p <= 2 || p > totalPages - 2 || Math.abs(p - page) <= 2).map((p, i, arr) => (
                <>
                  {i > 0 && arr[i - 1] !== p - 1 && <span style={{ padding: '7px 4px', color: 'var(--fg3)' }}>...</span>}
                  <button key={p} onClick={() => fetchSignins(p)} style={{ padding: '6px 10px', border: '1px solid var(--accent)', background: p === page ? 'var(--accent)' : 'transparent', color: p === page ? '#fff' : 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>{p}</button>
                </>
              ))}
              {page < totalPages && <Btn variant="outline" style={{ fontSize: 11 }} onClick={() => fetchSignins(page + 1)}>Next</Btn>}
            </div>
          )}
        </Card>
      )}

      {/* Parties tab */}
      {tab === 'parties' && (
        <Card>
          {parties.length === 0 ? <Empty message="No party records." /> : (
            <Table headers={['Date', 'Last Name', 'MBR #', 'Payer', 'Charcoal', 'Method', 'Total', 'Status', '']}>
              {parties.map(p => (
                <TR key={p.id}>
                  <TD>{p.party_date}</TD>
                  <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{p.lastname}</TD>
                  <TD>{p.membership_number || '—'}</TD>
                  <TD>{p.payer}</TD>
                  <TD>{p.charcoal_bags || '—'}</TD>
                  <TD><Badge variant={p.payment_method === 'card' ? 'blue' : 'green'}>{p.payment_method}</Badge></TD>
                  <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>${(p.total_charge || 0).toFixed(2)}</TD>
                  <TD><Badge variant={p.is_closed ? 'gray' : 'green'}>{p.is_closed ? 'Closed' : 'Open'}</Badge></TD>
                  <TD><a href={api.flaskUrl(`/party/${p.id}/pdf`)} style={{ fontSize: 11, color: 'var(--accent)' }}>PDF</a></TD>
                </TR>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* Guest log tab */}
      {tab === 'guests' && (
        <Card>
          {guests.length === 0 ? <Empty message="No guest records." /> : (
            <Table headers={['Name', 'Address', 'City', 'State', 'Visits', 'First Seen', 'Last Seen']}>
              {guests.map(g => (
                <TR key={g.id}>
                  <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{g.last_name}, {g.first_name}</TD>
                  <TD>{g.address || '—'}</TD>
                  <TD>{g.city || '—'}</TD>
                  <TD>{g.state || '—'}</TD>
                  <TD>{g.visit_count}</TD>
                  <TD>{g.first_seen || '—'}</TD>
                  <TD>{g.last_seen || '—'}</TD>
                </TR>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* Revenue tab */}
      {tab === 'revenue' && revenue && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[
            { label: 'Walk-in Sign-Ins', data: revenue.walkin, extra: `${revenue.walkin.transactions} transactions, ${revenue.walkin.guests} guests` },
            { label: 'Party Bookings', data: revenue.party, extra: `${revenue.party.bookings} bookings` },
          ].map(({ label, data, extra }) => (
            <Card key={label} style={{ padding: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg3)', marginBottom: 12 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 'bold', color: 'var(--fg)', marginBottom: 4 }}>${(data.total || 0).toFixed(2)}</div>
              <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 12 }}>{extra}</div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div><div style={{ fontSize: 11, color: 'var(--fg3)' }}>Cash</div><div style={{ fontWeight: 'bold' }}>${(data.cash || 0).toFixed(2)}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--fg3)' }}>Card</div><div style={{ fontWeight: 'bold' }}>${(data.card || 0).toFixed(2)}</div></div>
              </div>
            </Card>
          ))}
          <Card style={{ padding: 20, gridColumn: '1/-1', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 'bold', color: 'var(--fg2)' }}>Combined Total</span>
            <span style={{ fontSize: 28, fontWeight: 'bold', color: 'var(--success)' }}>${(revenue.combined_total || 0).toFixed(2)}</span>
          </Card>
        </div>
      )}
    </div>
  );
}
